"use client";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useRefinementList, useCurrentRefinements } from "react-instantsearch";
import {
  SingleSelectDropdownMenu,
  SingleSelectDropdownItem,
} from "@/components/SingleSelectDropdownMenu";
import { getFacetAttributeName } from "@/components/Search/utils/getFacetAttributeName";
import useIsMobile from "@/hooks/useIsMobile";
import { GetStartedWithFiltersSection } from "@/monarch/rules/getStartedWith/types";
import { cn } from "@/lib/utils";
import { useInView } from "react-intersection-observer";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  useGetStartedWithFieldEvents,
  GET_STARTED_ELEMENT_LOCATION,
  GET_STARTED_ELEMENT_TYPE,
  GET_STARTED_ELEMENT_HTML_ID,
  ELEMENT_NAMES,
} from "../hooks/useGetStartedWithEvents";

type FieldType = "Subject" | "Skill";

interface FilterDropdownProps {
  filterCategory: string;
  topLabel: string;
  placeholder: string;
  searchLabel: string;
  fieldType: FieldType;
  onSelectionChange?: (hasSelection: boolean) => void;
  onFieldSelected?: (fieldType: FieldType, fieldValue: string) => void;
  onItemsEmpty?: (isEmpty: boolean) => void;
}

const FilterDropdown = ({
  filterCategory,
  topLabel,
  placeholder,
  searchLabel,
  fieldType,
  onSelectionChange,
  onFieldSelected,
  onItemsEmpty,
}: FilterDropdownProps) => {
  const { items, refine } = useRefinementList({
    attribute: filterCategory,
    sortBy: ["count:desc", "name:asc"],
    limit: 100,
  });

  const isItemsEmpty = items.length === 0;

  // Notify parent when items become empty or non-empty
  useEffect(() => {
    onItemsEmpty?.(isItemsEmpty);
  }, [isItemsEmpty, onItemsEmpty]);

  const dropdownItems: SingleSelectDropdownItem[] = items.map((item) => ({
    label: item.label,
    value: item.value,
    count: item.count,
  }));

  const selectedValue = items.find((item) => item.isRefined)?.value || "";

  const handleSelect = (value: string) => {
    // Clear previous selection if any
    const currentRefined = items.find((item) => item.isRefined);
    if (currentRefined) {
      refine(currentRefined.value);
    }
    // Select new value if not empty
    if (value) {
      refine(value);
      // Track the field selection event
      onFieldSelected?.(fieldType, value);
    }
    // Notify parent about selection change
    onSelectionChange?.(!!value);
  };

  return (
    <div className="flex flex-col gap-2 w-full md:flex-1">
      <span className="elm-p4 text-primary-500">{topLabel}</span>
      <SingleSelectDropdownMenu
        label={placeholder}
        items={dropdownItems}
        selectedValue={selectedValue}
        onSelect={handleSelect}
        contentClassName="z-20"
        labelFontSize="elm-p3"
        height="h-[42px]"
        searchLabel={searchLabel}
      />
    </div>
  );
};

interface FiltersProps {
  indexName: string;
  showResults: boolean;
  onButtonClick: () => void;
  onFilterChange: () => void;
  filtersMonarchData: GetStartedWithFiltersSection;
  isCompact?: boolean;
  onSkillsEmptyChange?: (isEmpty: boolean) => void;
}

export const Filters = ({
  indexName,
  showResults,
  onButtonClick,
  onFilterChange,
  filtersMonarchData,
  isCompact = false,
  onSkillsEmptyChange,
}: FiltersProps) => {
  const { button: buttonLabel, filterLabels, title } = filtersMonarchData;
  const isMobile = useIsMobile();
  const hasViewedRef = useRef(false);
  const fieldViewedRef = useRef(false);
  const [skillsEmpty, setSkillsEmpty] = useState(false);

  const handleSkillsEmptyChange = useCallback(
    (isEmpty: boolean) => {
      setSkillsEmpty(isEmpty);
      onSkillsEmptyChange?.(isEmpty);
    },
    [onSkillsEmptyChange],
  );

  const { trackFieldSelected, trackFieldViewed } =
    useGetStartedWithFieldEvents();

  // Determine current button state
  const currentButtonText = showResults
    ? buttonLabel.editSelectionsButton1
    : buttonLabel.displayResultsButton;
  const currentButtonName = showResults
    ? ELEMENT_NAMES.EDIT_SELECTIONS
    : ELEMENT_NAMES.EXPLORE_COURSES;

  // Create webElement for current button state
  const webElement = useMemo(
    () => ({
      elementType: GET_STARTED_ELEMENT_TYPE,
      htmlId: GET_STARTED_ELEMENT_HTML_ID,
      location: GET_STARTED_ELEMENT_LOCATION,
      position: "1",
      name: currentButtonName,
      text: currentButtonText,
    }),
    [currentButtonName, currentButtonText],
  );

  const { elementClicked, elementViewed } = useElementEvents({ webElement });

  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  // Track button view when it comes into view
  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed]);

  // Track field viewed event once when component comes into view
  useEffect(() => {
    if (inView && !fieldViewedRef.current) {
      trackFieldViewed();
      fieldViewedRef.current = true;
    }
  }, [inView, trackFieldViewed]);

  // Reset view tracking when button state changes
  useEffect(() => {
    hasViewedRef.current = false;
  }, [showResults]);

  const { items: currentRefinements, refine } = useCurrentRefinements();

  const subjectAttribute = getFacetAttributeName("subject", { indexName });
  const skillsAttribute = getFacetAttributeName("skills.skill", { indexName });

  const subjectSelected = currentRefinements.some(
    (item) =>
      item.attribute === subjectAttribute && item.refinements.length > 0,
  );

  const skillSelected = currentRefinements.some(
    (item) => item.attribute === skillsAttribute && item.refinements.length > 0,
  );

  // Enable button if subject is selected AND (skill is selected OR skills dropdown is empty)
  const allFiltersSelected = subjectSelected && (skillSelected || skillsEmpty);

  const clearAllFilters = () => {
    currentRefinements.forEach((item) => {
      item.refinements.forEach((refinement) => {
        refine(refinement);
      });
    });
  };

  const handleSubjectChange = () => {
    // Any change to subject filter should clear the skills filter
    // Notify parent that filter changed (to hide results until "Explore courses" is clicked)
    onFilterChange();
  };

  const handleSkillsChange = () => {
    // Notify parent that filter changed (to hide results until "Explore courses" is clicked)
    onFilterChange();
  };

  const handleButtonClick = () => {
    // Track button click event
    elementClicked();

    if (showResults) {
      // Clear all filters when clicking "Edit selections"
      clearAllFilters();
    }
    onButtonClick();
  };

  // Get the selected subject value for the dynamic title
  const selectedSubject = currentRefinements.find(
    (item) => item.attribute === subjectAttribute,
  )?.refinements[0]?.value as string | undefined;

  const dynamicTitle = showResults
    ? `${title.dynamicTitlePrefix} ${selectedSubject}`
    : title.startingTitle;

  return (
    <>
      <div className="flex flex-col items-center">
        <h2
          id="get-started-title"
          className={`elm-d1 italic font-black text-primary-500 text-center`}
        >
          {dynamicTitle}
        </h2>
      </div>
      <div
        className={cn(
          "w-full flex flex-col items-stretch gap-4 pb-16",
          isCompact
            ? "flex-col"
            : "md:flex-row md:items-center md:justify-center",
        )}
      >
        <FilterDropdown
          filterCategory={subjectAttribute}
          topLabel={filterLabels.subject}
          placeholder="Select a subject"
          onSelectionChange={handleSubjectChange}
          searchLabel="subject"
          fieldType="Subject"
          onFieldSelected={trackFieldSelected}
        />
        <FilterDropdown
          filterCategory={skillsAttribute}
          topLabel={filterLabels.skill}
          placeholder="Select a skill"
          onSelectionChange={handleSkillsChange}
          searchLabel="skills"
          fieldType="Skill"
          onFieldSelected={trackFieldSelected}
          onItemsEmpty={handleSkillsEmptyChange}
        />
        <div
          className={cn(
            "flex flex-col gap-2 justify-end h-full md:flex-shrink-0",
            !isCompact ? "w-full md:w-[169px]" : "w-full",
          )}
        >
          <span className="hidden md:block text-sm text-transparent">
            &nbsp;
          </span>
          <button
            ref={inViewRef}
            disabled={!allFiltersSelected}
            onClick={handleButtonClick}
            className={`w-full h-[42px] rounded-full px-6 gap-2 font-medium whitespace-nowrap transition-opacity border-2 flex items-center justify-center ${
              showResults && allFiltersSelected && !isMobile
                ? "bg-white text-brand border-brand hover:opacity-90 cursor-pointer"
                : allFiltersSelected
                  ? "bg-brand text-white border-brand hover:opacity-90 cursor-pointer"
                  : "bg-brand text-white border-brand opacity-50 cursor-not-allowed"
            }`}
          >
            {showResults && allFiltersSelected
              ? `${buttonLabel.editSelectionsButton1}`
              : `${buttonLabel.displayResultsButton}`}
          </button>
        </div>
      </div>
    </>
  );
};
